import { useEffect, useState } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { OIDC_DEFAULT_SCOPES } from "@azure/msal-browser";
import { People } from "@mui/icons-material";
import FormViewCommandBar from "../commandBar/FormViewCommandBar";
import Page from "../layout/Page";
import ConfirmDialog from "../form/ConfirmDialog";
import { useNavigate, useParams } from "react-router-dom";
import { useRole } from "../auth/role";
import axios from "axios";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { ApiGateway } from "../config/config";
import { handleFieldChange } from "../form/Form";

const userStub = {
  koId: "",
  email: "",
  firstName: "",
  lastName: "",
  role: { id: 0, name: "" },
};

export default function UserFormPage(props = {}) {
  const params = useParams();
  const { instance, accounts } = useMsal();
  const account = useAccount(instance.getActiveAccount() || {});
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [saveClicked, setSaveClicked] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [user, setUser] = useState(userStub);
  const [initialized, setInitialized] = useState(false);

  const getUserByKoId = async (koId) => {
    const account = instance.getActiveAccount();
    const token = await instance.acquireTokenSilent({
      scopes: OIDC_DEFAULT_SCOPES,
      account,
      forceRefresh: true,
    });

    const basePath = `${ApiGateway.profile}/${koId}`;
    const options = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token.idToken}`,
      },
    };
    const response = await axios.get(basePath, options);
    if (response.data) {
      const u = response.data;
      const _u = {};
      _u.koId = koId;
      _u.email = u.email;
      _u.firstName = u.firstName;
      _u.lastName = u.lastName;
      _u.role = u.roles[0];
      console.log("setting user", _u);
      setUser(_u);
    }
  };

  const { ROLES, hasRole, hasAnyRole, getRoleById } = useRole();

  const showDialog = () => {
    setConfirmDialog(true);
  };
  const hideDialog = () => {
    setConfirmDialog(false);
  };

  const breadcrumb = (
    <span>
      <People></People>Edit MFI User
    </span>
  );

  const onSave = () => {
    console.log("UserFormPage onSave");
    setSaveClicked(true);
    setTimeout(() => {
      setSaveClicked(false);
    }, 1000);
  };

  const confirmCancel = () => {
    console.log("Cancel");
    if (isDirty) {
      showDialog();
    } else {
      handleCancel();
    }
  };

  const handleCancel = () => {
    navigate("/users/list");
  };

  const startLoading = () => {
    setIsLoading(true);
  };

  const stopLoading = () => {
    setIsLoading(false);
  };

  const getDirectoryUser = async (search, filterBy) => {
    if (account) {
      instance
        .acquireTokenSilent({
          scopes: OIDC_DEFAULT_SCOPES,
          account: account,
          forceRefresh: true,
        })
        .then(async (response) => {
          if (response) {
            const headers = {
              "Content-Type": "application/json",
              Authorization: `Bearer ${response.accessToken}`,
            };
            // const attributes = ['mail','givenName','surname','koId'].join(',')
            const searchResponse = await axios.get(
              `https://graph.microsoft.com/v1.0/users?$select=*&$filter=${filterBy} eq '${search}'`,
              { headers: headers },
            );
            console.log("searchResponse: ", searchResponse);
            const _user = searchResponse.data?.value.length
              ? searchResponse.data?.value[0]
              : undefined;

            console.log("_user: ", _user);

            if (_user) {
              let _koId = "123";
              if (
                _user.userPrincipalName.includes("ko.com") &&
                _user.userPrincipalName.includes("always")
              ) {
                _koId = _user.userPrincipalName.split("@")[0];
              }

              setUser({
                ...user,
                email: _user.mail,
                firstName: _user.givenName,
                lastName: _user.surname,
                koId: _koId,
              });
            }
          }
        });
    }
  };

  const handleLookupClick = () => {
    getDirectoryUser(user.email, "mail");
  };

  const handleRoleSelectChange = async (event) => {
    const rid = event.target.value;
    const _role = getRoleById(rid);
    if (_role) {
      setUser({ ...user, role: { id: _role.id, name: _role.name } });
    } else {
      setUser({ ...user, role: { id: 0, name: "" } });
    }
    setDirty(true);
  };

  useEffect(() => {
    if (params.koId && !initialized) {
      (async () => await getUserByKoId(params.koId))();
      setInitialized(true);
    }
  }, []);

  return (
    <Page
      {...props}
      breadcrumb={breadcrumb}
      isLoading={isLoading}
      commandBar={
        <FormViewCommandBar
          onSave={onSave}
          onCancel={confirmCancel}
          enableSave={isDirty}
        />
      }
    >
      <ConfirmDialog
        title="Discard all changes?"
        text="You will not be able to recover your work."
        open={confirmDialog}
        hideDialog={hideDialog}
        handleConfirm={handleCancel}
        yesLabel="Discard"
        noLabel="Stay on Page"
      />
      <Grid container spacing={2}>
        <Grid item xs={4} md={4}>
          <TextField
            disabled
            label="KO ID"
            fullWidth
            id="koId_input"
            value={user.koId}
            size="small"
          />
        </Grid>
        <Grid item xs={6} md={6}>
          <TextField
            label="Email"
            fullWidth
            id="email_input"
            value={user.email}
            size="small"
            onChange={(event) =>
              handleFieldChange(
                "email",
                event.target.value,
                user,
                setUser,
                setDirty,
              )
            }
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <Button onClick={handleLookupClick} variant="outlined">
            Lookup from AD
          </Button>
        </Grid>
        <Grid item xs={6} md={6}>
          <TextField
            label="First Name"
            fullWidth
            id="firstName_input"
            value={user.firstName}
            size="small"
            onChange={(event) =>
              handleFieldChange(
                "firstName",
                event.target.value,
                user,
                setUser,
                setDirty,
              )
            }
          />
        </Grid>
        <Grid item xs={6} md={6}>
          <TextField
            label="Last Name"
            fullWidth
            id="lastName_input"
            value={user.lastName}
            size="small"
            onChange={(event) =>
              handleFieldChange(
                "lastName",
                event.target.value,
                user,
                setUser,
                setDirty,
              )
            }
          />
        </Grid>
        <Grid item xs={6} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Role:</InputLabel>
            <Select
              size="small"
              fullWidth
              id={`role_select`}
              value={user.role.id}
              onChange={handleRoleSelectChange}
            >
              <MenuItem value={0}>Select A Role</MenuItem>
              {hasRole([ROLES.ADMIN]) && (
                <MenuItem value={ROLES.ADMIN.id}>{ROLES.ADMIN.name}</MenuItem>
              )}
              {hasRole([ROLES.ADMIN, ROLES.MFI_ADMIN]) && (
                <MenuItem value={ROLES.MFI_ADMIN.id}>
                  {ROLES.MFI_ADMIN.name}
                </MenuItem>
              )}
              {hasRole([ROLES.ADMIN, ROLES.MFI_ADMIN]) && (
                <MenuItem value={ROLES.QSE_MGR.id}>
                  {ROLES.QSE_MGR.name}
                </MenuItem>
              )}
              {hasAnyRole() && !hasRole([ROLES.BOTTLER]) && (
                <MenuItem value={ROLES.BOTTLER.id}>
                  {ROLES.BOTTLER.name}
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Page>
  );
}
